import React, { useLayoutEffect, useRef, useState } from 'react';
import { useSiteBanner } from '../hooks/useSiteBanner';
import { isBannerHtmlEmpty } from '../utils/bannerHtml';
import { cn } from '../lib/utils';

function BannerHtmlContent({ html, className, contentRef }) {
  return (
    <div
      ref={contentRef}
      className={cn('site-banner-content shrink-0', className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

const HeaderBannerTicker = ({
  className,
  html: htmlOverride,
  enabled: enabledOverride,
  scrollEnabled: scrollEnabledOverride,
  scrollSpeedSeconds: scrollSpeedOverride,
}) => {
  const live = useSiteBanner();
  const html = htmlOverride != null ? htmlOverride : live.html;
  const enabled = enabledOverride != null ? enabledOverride : live.enabled;
  const scrollEnabled = scrollEnabledOverride != null
    ? scrollEnabledOverride
    : live.scrollEnabled;
  const scrollSpeedSeconds = scrollSpeedOverride != null
    ? scrollSpeedOverride
    : live.scrollSpeedSeconds;
  const loading = htmlOverride == null && live.loading;

  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [scrollMetrics, setScrollMetrics] = useState({ startOffset: 0, copyWidth: 0 });

  useLayoutEffect(() => {
    if (!scrollEnabled) return undefined;

    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return undefined;

    const measure = () => {
      const contentWidth = content.getBoundingClientRect().width;
      const containerWidth = container.getBoundingClientRect().width;
      setScrollMetrics({
        startOffset: containerWidth,
        copyWidth: contentWidth,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(content);
    return () => observer.disconnect();
  }, [html, scrollEnabled]);

  if (loading || !enabled || isBannerHtmlEmpty(html)) return null;

  const shouldScroll = scrollEnabled;
  const canAnimate = shouldScroll && scrollMetrics.copyWidth > 0 && scrollMetrics.startOffset > 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative min-w-0 overflow-hidden text-sm text-muted-foreground',
        className
      )}
      aria-live="off"
    >
      <div
        className={cn(
          'flex w-max items-center',
          canAnimate ? 'site-banner-track is-scrolling' : 'mx-auto'
        )}
        style={canAnimate ? {
          '--site-banner-start': `${scrollMetrics.startOffset}px`,
          '--site-banner-copy-width': `${scrollMetrics.copyWidth}px`,
          '--site-banner-duration': `${scrollSpeedSeconds}s`,
        } : undefined}
      >
        <BannerHtmlContent html={html} contentRef={contentRef} />
      </div>
    </div>
  );
};

export function HeaderBannerRow() {
  const live = useSiteBanner();
  if (live.loading || !live.enabled || isBannerHtmlEmpty(live.html)) return null;

  return (
    <div className="px-4 pb-1.5">
      <HeaderBannerTicker className="w-full" />
    </div>
  );
}

export default HeaderBannerTicker;
